import { z } from 'zod';
import { dateInputSchema, emailSchema, isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * A customer, a named staff member, and a slot of time — a trial fitting, a measurement session,
 * a delivery handover.
 *
 * TWO RULES HOLD THE WHOLE MODULE UP, and everything else here is detail hanging off them.
 *
 * 1. AN APPOINTMENT IS SCOPED TO A STAFF MEMBER. `staffEmail` is required; there is no
 *    unassigned appointment, because "somebody is coming in at four" is not something a shop can
 *    act on. Staff is the scoping and filtering axis and NOT a calendar column: the week grid
 *    puts days across the top and half-hour slots down the side, and the staff filter narrows
 *    what that one grid shows. A column per tailor stops fitting on a phone at three tailors.
 *
 * 2. TWO APPOINTMENTS FOR THE SAME STAFF MEMBER MAY NOT OVERLAP. Time is a HALF-OPEN interval
 *    `[startAt, endAt)` — one ending at 11:00 and one starting at 11:00 do not overlap, which is
 *    the only reading under which back-to-back fittings are bookable at all. A `cancelled`
 *    appointment never blocks a slot and is excluded from the check; every other status blocks,
 *    including `no_show` — the hour was held and lost, and pretending otherwise would let a
 *    double booking in through the back door.
 *
 * The overlap test itself is exported from here — see {@link appointmentsOverlap} — so the
 * server's conflict detection and the client's warning cannot drift into disagreeing about what
 * a clash is. One of them saying "free" while the other says "taken" is worse than neither.
 */

/* -------------------------------------------------------------------------------------------
 * Status
 * ---------------------------------------------------------------------------------------- */

/**
 * Where an appointment has got to. New ones are `scheduled`; the other three are what the shop
 * marks afterwards. `no_show` is its own outcome rather than a flavour of `cancelled` because
 * the shop held the slot and nobody came — a fact worth being able to count per customer.
 */
export const APPOINTMENT_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];
export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);

export const APPOINTMENT_PURPOSE_MAX_LENGTH = 120;
export const APPOINTMENT_NOTES_MAX_LENGTH = 500;
export const APPOINTMENT_CANCEL_REASON_MAX_LENGTH = 200;

/* -------------------------------------------------------------------------------------------
 * The shape of the week grid
 * ---------------------------------------------------------------------------------------- */

/** The width of one row in the grid, and the unit every booking is a whole number of. */
export const APPOINTMENT_SLOT_MINUTES = 30;
/** 10:00, in minutes from local midnight. The first row of the grid. */
export const APPOINTMENT_DAY_START_MINUTE = 600;
/** 22:00, in minutes from local midnight. Exclusive — the last row STARTS at 21:30. */
export const APPOINTMENT_DAY_END_MINUTE = 1320;
/** Four hours. Past this it is a day's work, not an appointment. */
export const APPOINTMENT_MAX_DURATION_MINUTES = 240;

/**
 * Every slot the grid draws, `'10:00'` through `'21:30'` — 24 of them.
 *
 * Derived rather than typed out so the three constants above stay the single description of the
 * shop day: change the end minute and the array follows.
 */
export const APPOINTMENT_SLOT_STARTS: readonly string[] = Object.freeze(
  Array.from(
    {
      length:
        (APPOINTMENT_DAY_END_MINUTE - APPOINTMENT_DAY_START_MINUTE) / APPOINTMENT_SLOT_MINUTES,
    },
    (_unused, index) => {
      const minuteOfDay = APPOINTMENT_DAY_START_MINUTE + index * APPOINTMENT_SLOT_MINUTES;
      const hour = String(Math.floor(minuteOfDay / 60)).padStart(2, '0');
      const minute = String(minuteOfDay % 60).padStart(2, '0');
      return `${hour}:${minute}`;
    },
  ),
);

/* -------------------------------------------------------------------------------------------
 * Overlap
 * ---------------------------------------------------------------------------------------- */

const epochMsOf = (at: Date | string): number =>
  at instanceof Date ? at.getTime() : new Date(at).getTime();

/**
 * Do two time intervals collide?
 *
 * `[startAt, endAt)` is half-open, so touching intervals do NOT collide — 10:00–11:00 and
 * 11:00–12:00 are two bookable fittings and not a clash. The comparison is on epoch
 * milliseconds and never on the strings themselves, because `Date`s and ISO strings both arrive
 * here and two ISO strings for the same instant can differ character for character (`Z` against
 * `+05:30`, milliseconds present or absent) while comparing perfectly well as numbers.
 *
 * This is TIME ONLY. It knows nothing about who the appointment is for or what state it is in:
 * the caller filters to one staff member and drops `cancelled` rows before asking. Keeping those
 * two decisions out here is what lets the server's conflict check and the client's inline warning
 * share the one predicate.
 */
export function appointmentsOverlap(
  a: { startAt: Date | string; endAt: Date | string },
  b: { startAt: Date | string; endAt: Date | string },
): boolean {
  const aStart = epochMsOf(a.startAt);
  const aEnd = epochMsOf(a.endAt);
  const bStart = epochMsOf(b.startAt);
  const bEnd = epochMsOf(b.endAt);
  return aStart < bEnd && bStart < aEnd;
}

/* -------------------------------------------------------------------------------------------
 * The stored appointment
 * ---------------------------------------------------------------------------------------- */

export const appointmentSchema = z.object({
  id: objectIdSchema,

  /** Who is coming in. The name is denormalised so a calendar cell needs no second read. */
  customerId: objectIdSchema,
  customerName: z.string(),

  /** Who is seeing them. Required — see the module note above. */
  staffEmail: z.string(),
  /** Denormalised for the same reason, and for the staff filter's own list of names. */
  staffName: z.string().nullable(),

  startAt: isoDateStringSchema,
  /** Exclusive. See {@link appointmentsOverlap}. */
  endAt: isoDateStringSchema,
  /**
   * Server-computed from the two instants above, so the grid never reproduces the arithmetic to
   * decide how many rows tall to draw a booking.
   */
  durationMinutes: z.number().int().positive(),

  /** Free text — what the cell shows. Deliberately not an enum: every shop books its own things. */
  purpose: z.string(),
  status: appointmentStatusSchema,
  notes: z.string().nullable(),

  completedAt: isoDateStringSchema.nullable(),
  cancelledAt: isoDateStringSchema.nullable(),
  /** Stamped when the reminder actually went out, so it goes out once and the screen can say so. */
  reminderSentAt: isoDateStringSchema.nullable(),
  cancelReason: z.string().nullable(),

  createdByUserId: z.string().nullable(),
  createdByName: z.string().nullable(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Appointment = z.infer<typeof appointmentSchema>;

/* -------------------------------------------------------------------------------------------
 * Reading a week
 * ---------------------------------------------------------------------------------------- */

/**
 * What the calendar asks for. It reads a week at a time and does NOT paginate — a page boundary
 * inside a week would cut the grid in half — so this pointedly does not extend
 * `paginationQuerySchema`.
 */
export const appointmentWeekQuerySchema = z.object({
  /** The Monday the grid opens on. The week runs Monday through Sunday. */
  weekStart: dateInputSchema,
  /** Omitted means every staff member's appointments for that week. */
  staffEmail: emailSchema.optional(),
});
export type AppointmentWeekQuery = z.infer<typeof appointmentWeekQuerySchema>;
export type AppointmentWeekQueryInput = z.input<typeof appointmentWeekQuerySchema>;

/**
 * What comes back: a FLAT list, not a grid.
 *
 * Grouping into day columns and slot rows is the web app's job, because only the browser knows
 * the viewer's timezone and only the component knows how it wants to lay a long booking out
 * across the rows it spans. A server that shipped a pre-grouped structure would be guessing at
 * both.
 */
export const appointmentWeekSchema = z.object({
  weekStart: isoDateStringSchema,
  /** Exclusive — the following Monday. */
  weekEnd: isoDateStringSchema,
  items: z.array(appointmentSchema),
});
export type AppointmentWeek = z.infer<typeof appointmentWeekSchema>;

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

const isHalfHourAligned = (at: Date): boolean =>
  at.getMinutes() % APPOINTMENT_SLOT_MINUTES === 0 &&
  at.getSeconds() === 0 &&
  at.getMilliseconds() === 0;

const isBookableDuration = (minutes: number): boolean =>
  minutes > 0 &&
  minutes % APPOINTMENT_SLOT_MINUTES === 0 &&
  minutes <= APPOINTMENT_MAX_DURATION_MINUTES;

const DURATION_MESSAGE = `Choose a length in ${APPOINTMENT_SLOT_MINUTES}-minute steps, up to ${APPOINTMENT_MAX_DURATION_MINUTES} minutes`;
const ALIGNMENT_MESSAGE = 'Appointments start on the hour or the half hour';

/**
 * A booking is a SLOT START PLUS A LENGTH, never two instants.
 *
 * That removes the "end is before the start" error class outright rather than validating against
 * it: there is no field a caller could put the wrong instant in. The server derives `endAt` and
 * `durationMinutes` on the stored document from these two.
 */
const createAppointmentObject = z.object({
  customerId: objectIdSchema,
  staffEmail: emailSchema,
  startAt: dateInputSchema,
  durationMinutes: z.number().int().default(APPOINTMENT_SLOT_MINUTES),
  purpose: z.string().trim().min(1).max(APPOINTMENT_PURPOSE_MAX_LENGTH),
  notes: z.string().trim().max(APPOINTMENT_NOTES_MAX_LENGTH).nullish(),
});

/**
 * The grid constants above describe what the calendar RENDERS and are deliberately not enforced
 * here — a shop that ran late and finished a fitting at half past ten at night must still be able
 * to record the appointment it actually had, and a schema that refused would only teach the shop
 * to lie about the time. What is enforced is half-hour alignment, because a booking that starts
 * at 10:07 cannot be drawn in a grid of half-hour rows at all.
 */
export const createAppointmentSchema = createAppointmentObject
  .refine((input) => isBookableDuration(input.durationMinutes), {
    message: DURATION_MESSAGE,
    path: ['durationMinutes'],
  })
  .refine((input) => isHalfHourAligned(input.startAt), {
    message: ALIGNMENT_MESSAGE,
    path: ['startAt'],
  });
export type CreateAppointmentInput = z.input<typeof createAppointmentSchema>;

/**
 * Moving or re-purposing a booking. No `customerId`: an appointment given to a different customer
 * is a different appointment, and quietly rewriting who is coming in would rewrite history the
 * shop may have already acted on.
 */
export const updateAppointmentSchema = createAppointmentObject
  .omit({ customerId: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' })
  .refine(
    (patch) => patch.durationMinutes === undefined || isBookableDuration(patch.durationMinutes),
    {
      message: DURATION_MESSAGE,
      path: ['durationMinutes'],
    },
  )
  .refine((patch) => patch.startAt === undefined || isHalfHourAligned(patch.startAt), {
    message: ALIGNMENT_MESSAGE,
    path: ['startAt'],
  });
export type UpdateAppointmentInput = z.input<typeof updateAppointmentSchema>;

/** Marking what became of the slot: they came, they cancelled, they never turned up. */
export const setAppointmentStatusSchema = z
  .object({
    status: appointmentStatusSchema,
    cancelReason: z.string().trim().max(APPOINTMENT_CANCEL_REASON_MAX_LENGTH).nullish(),
  })
  .refine(
    (input) =>
      input.status === 'cancelled' ||
      input.cancelReason === null ||
      input.cancelReason === undefined ||
      input.cancelReason === '',
    { message: 'A reason only applies to a cancellation', path: ['cancelReason'] },
  );
export type SetAppointmentStatusInput = z.input<typeof setAppointmentStatusSchema>;

/* -------------------------------------------------------------------------------------------
 * The conflict
 * ---------------------------------------------------------------------------------------- */

/**
 * What a 409 from this module carries in the standard `apiErrorSchema` envelope's `code`.
 *
 * Module-specific rather than the generic `conflict` so the web app can tell a double booking
 * apart from every other kind of clash and render the one below instead of a shrug.
 */
export const APPOINTMENT_CONFLICT_ERROR_CODE = 'appointment_overlap';

/**
 * What goes in that error's `details`: the staff member who is already busy, and the bookings
 * that say so.
 *
 * Naming the clashing appointment is the entire point. "Conflict" makes the shop go hunting
 * through the week to find out what it clashed with; "Priya Nair, 16:00–17:00" is a sentence
 * somebody can act on without leaving the form. `.min(1)` because a conflict with nothing in it
 * is not a conflict, and would render as an empty list.
 */
export const appointmentConflictSchema = z.object({
  staffEmail: z.string(),
  conflicts: z
    .array(
      z.object({
        id: objectIdSchema,
        customerName: z.string(),
        startAt: isoDateStringSchema,
        endAt: isoDateStringSchema,
      }),
    )
    .min(1),
});
export type AppointmentConflict = z.infer<typeof appointmentConflictSchema>;
